<script setup lang="ts">
const { $kyc } = useNuxtApp();
const { t } = useTexts();

const integrationType = computed(() => {
  if (!$kyc.operatorName?.length) {
    return '';
  }

  const firstLetter = $kyc.operatorName.charAt(0).toUpperCase();

  return firstLetter;
});

const { isReproved, isProcessing, isFullOnboarding } = useKyc();

const { fetchUserProfile, forceKycRequestMessage, userDocument, userId } =
  useUserData();

async function onVerifyButtonClick() {
  await fetchUserProfile({ force: true });
}

const titleText = computed(() => {
  if (
    isWithdrawFlowOpen.value ||
    $kyc.current.source === 'password_recovery_attempt_limit'
  ) {
    return t('verification.kyc.title_facial');
  }

  if (isFullOnboarding.value) {
    return t('verification.kyc.title');
  }

  return t('verification.kyc.title_facial');
});

const descriptionText = computed(() => {
  if (isWithdrawFlowOpen.value) {
    return t('verification.kyc.desc_withdraw');
  }

  if ($kyc.current.source === 'password_recovery_attempt_limit') {
    return t('verification.kyc.desc_attempt_limit');
  }

  if (forceKycRequestMessage.value) {
    return forceKycRequestMessage.value;
  }

  if (isFullOnboarding.value) {
    return t('verification.kyc.desc');
  }

  return t('verification.kyc.desc_facial');
});

onMounted(() => {
  const { $kyc } = useNuxtApp();

  $kyc.resetOperator();
});
</script>

<template>
  <div :class="$style.boxContainer">
    <img
      alt="KYC icon"
      aria-hidden="true"
      :class="$style.modalIcon"
      :src="getAssetsUrlForCDN('/assets/images/svg/kyc.svg')"
    />

    <p :class="$style.title">
      {{ titleText }}
    </p>

    <p
      :class="$style.description"
      v-html="descriptionText"
    />

    <BaseKycButton v-if="!isProcessing">
      <template v-if="isReproved">
        <span>Tentar novamente</span>
        <NuxtIcon name="arrows/angle_right" />
      </template>
    </BaseKycButton>

    <button
      v-else
      :class="$style.verifyButton"
      @click="onVerifyButtonClick"
    >
      Verificar status novamente
    </button>

    <div
      v-if="userDocument || userId"
      :class="$style.userInfos"
    >
      <div
        v-if="userDocument"
        :class="$style.userInfoItem"
      >
        <span :class="$style.userInfoItemLabel">CPF:</span>
        <span :class="$style.userInfoItemValue">
          {{ userDocument }}
        </span>
      </div>

      <div
        v-if="userId"
        :class="$style.userInfoItem"
      >
        <span :class="$style.userInfoItemLabel">ID:</span>
        <span :class="$style.userInfoItemValue">
          {{ userId }}
        </span>
      </div>

      <div
        v-if="integrationType"
        :class="$style.userInfoItem"
      >
        <span :class="$style.userInfoItemValue">
          <span :class="$style.integrationType"> [{{ integrationType }}] </span>
        </span>
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
.boxContainer {
  @apply p-3 md:p-6 flex flex-col justify-center items-center flex-1 text-texts text-center;

  .modalIcon {
    @apply w-28 h-28 mx-auto mb-5;
  }

  .title {
    @apply font-bold mb-3;
  }

  .description {
    @apply max-w-80 inline-block mx-auto text-sm text-texts/80 mb-6;

    strong {
      @apply text-primary/80;
    }
  }

  .verifyButton {
    @apply mx-auto rounded-md gap-1.5 flex items-center text-xs font-semibold uppercase py-2.5 px-4 focus:outline-none focus:ring-4 appearance-none bg-auth-texts/10 text-auth-texts/90;
  }

  .userInfos {
    @apply inline-flex gap-5 justify-center bg-auth-texts/10 rounded-md py-2 px-3 mt-4 text-2xs;

    .userInfoItem {
      @apply flex items-center gap-1;

      .userInfoItemLabel {
        @apply text-2xs text-texts/40;
      }

      .userInfoItemValue {
        @apply text-2xs text-texts/70 font-semibold;

        .integrationType {
          @apply text-2xs text-texts/40 font-semibold;
        }
      }
    }
  }
}
</style>
